'use client'

import { useEffect, useRef, useState } from 'react'
import styles from './DeleteAccountModal.module.css'
import { FieldState, ValidationsForForm } from '@/interfaces/api/forms/forms_types'
import { isRequired } from '@/utils/validators'
import { getFieldError, toBase64, validateForm } from '@/utils/helpers'
import Button, { ButtonKind, ButtonSize } from '@/components/UI/Buttons/Button/Button'
import { useLocale, useTranslations } from 'next-intl'
import TooltipField from '@/components/UI/Tooltips/TooltipField/TooltipField'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import IconCryingFace from '@/assets/img/Icon_crying_face.svg'
import IconShowPassword from '@/assets/img/Icon_Show-Password.svg'
import IconHidePassword from '@/assets/img/Icon_Hide-Password.svg'
import { useSelf } from '@/apiUtilities/queries/useSelf'
import { API_Error } from '@/interfaces/api/response/api_response_types'
import { useDeleteAccount } from '@/apiUtilities/mutations/useDeleteAccount'
import { DeleteUserErrorsTypes } from '@/interfaces/api/self/api_request_types'
import { toast } from 'react-toastify'
import Notification from '@/components/Notification/Notification'
import { useUserStore } from '@/stores/useUserStore'
import { useModalStore } from '@/stores/useModalStore'
import { ModalTypes } from '@/interfaces/api/generic/api_modals_types'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@/i18n/navigation'
import { useExitSurvey } from '@/apiUtilities/queries/useExitSurvey'
import ExitSurveyModal from '../ExitSurveyModal/ExitSurveyModal'

interface DeleteAccountModalProps extends React.HTMLAttributes<HTMLDivElement> {
}

interface DeleteAccountFormState {
  password: FieldState<string>
}

/**
 * Initial (empty) validations object for the delete account form.
 */
const initialValidations: ValidationsForForm<DeleteAccountFormState> = {
  password: []
}

export default function DeleteAccountModal ({ ...rest }: DeleteAccountModalProps) {
  const t = useTranslations('Modals.DeleteAccount')
  const locale = useLocale()
  const queryClient = useQueryClient()
  const router = useRouter()
  const deleteAccount = useDeleteAccount()
  const { openModal, closeModal } = useModalStore()
  const { logout } = useUserStore()
  const { data: self } = useSelf()
  const { data: checkExitSurveyData } = useExitSurvey(locale)
  const passwordRef = useRef<HTMLInputElement | null>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showServerError, setShowServerError] = useState<string>('')
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [formState, setFormState] = useState<DeleteAccountFormState>({
    password: {
      value: '',
      dirty: false,
      required: true,
      validators: [
        { validator: isRequired, show: false }
      ]
    }
  })
  const [validations, setValidations] = useState<ValidationsForForm<DeleteAccountFormState>>(initialValidations)

  /**
	 * Handles input changes for all fields.
	 * Marks the field as `dirty` and updates its `value`.
	 * @param e Input change event from the TextField.
	 */
	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		const key = e.target.name as keyof DeleteAccountFormState
    if (!!showServerError) setShowServerError('')
		setFormState(formStatePrev => {
			return {
				...formStatePrev,
				[key]: {
					...formStatePrev[key],
					value: e.target.value.trim(),
					dirty: true
				}
			}
		})
	}

  /**
   * Cancel the delete account and close the modal
   * 
   * @param e - Mouse click event triggered by cancel button
   */
  function handleCancel (e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    closeModal()
  }

  /**
   * Show the exit survey modal
   */
  function showExitSurveyModal () {
    openModal({
      type: ModalTypes.LATERAL,
      content: <ExitSurveyModal endpointData={checkExitSurveyData!} />,
      closeOnBackdrop: false
    })
  }

  /**
   * Handles change password API errors and updates UI state accordingly
   *
   * @param response - The API error response returned by the backend
   */
  async function handleError (response: API_Error) {
    if(response.code === DeleteUserErrorsTypes.UNAUTHENTICATED || response.code === DeleteUserErrorsTypes.UNAUTHORIZED) {
      setShowServerError('unauthenticated')
      passwordRef.current?.select()
      return
    }

    toast.error(<Notification text={t('errors.server.unknow')} />)
  }

  /**
   * Runs after a success delete account
   */
  async function handleSuccess () {
    // clear cache
    await queryClient.cancelQueries()
    queryClient.clear()
    closeModal()
    logout()
    if (checkExitSurveyData!.offer_survey) showExitSurveyModal()
    router.push('/home')
  }

  /**
   * Submits the account deletion request
   * 
   * @param e - Mouse click event triggered by accept button
   */
  function handleSubmit (e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()

    if (!self || !checkExitSurveyData) return
    
    const basicAuth: string = toBase64(`${self.username}:${formState.password.value.trim()}`)
    
    deleteAccount.mutate({ basicAuth }, {
      onSuccess: handleSuccess,
      onError: handleError
    })
  }

  /**
   * Re-validates the form whenever a field value changes
   * Updates both:
   * - `validations` (to show messages in the tooltips)
   * - `formIsValid` (to enable/disable the submit button)
   */
  useEffect(() => {
    const { validations: preValidations, isValid } = validateForm(formState, initialValidations)
    setValidations(preValidations)
    setFormIsValid(isValid)
  }, [formState])

  const passwordError = getFieldError<DeleteAccountFormState>(formState, validations, 'password')
  
  return (
    <div className={styles.deleteAccountModalWrapper} {...rest}>
      <div className={styles.animationWrapper}>
        <IconCryingFace />
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.endMessageWrapper}>
          <h1 className={`title2`}>{t('title')}</h1>
          <p className={`bodyTextMedium`}>{t('content')}</p>
        </div>

        <form className={styles.formWrapper}>
          <p className={`bodyTextMedium ${styles.formContent}`}>{t('formContent')}</p>

          <div className={styles.fields}>
            <TooltipField
              validations={validations.password}
              placement="bottom-start"
              arrow={false}
              disableHoverListener={true}
              disableTouchListener={true}
              hasHelperText={true}
            >
              <TextField
                inputRef={passwordRef}
                type={showPassword ? 'text' : 'password'}
                name="password"
                label={t('passwordLabel')}
                value={formState.password.value}
                error={passwordError.hasError || !!showServerError}
                helperText={!!showServerError ? t(`errors.server.${showServerError}`) : ' '}
                autoFocus={true}
                onChange={handleChange}
                slotProps={{
                  input: {
                    endAdornment:
                    <InputAdornment position="end">
                     <IconButton
                        type={'button'}
                        onClick={() => setShowPassword(prev => !prev)}
                        edge="end"
                      >
                        {showPassword
                          ? <IconHidePassword className={styles.passwordIcon} />
                          : <IconShowPassword className={styles.passwordIcon} />
                        }
                      </IconButton>
                    </InputAdornment>
                  }
                }}
              />
            </TooltipField>
          </div>
        </form>
      </div>

      <div className={styles.buttonsWrapper}>
        <Button kind={ButtonKind.PRIMARY} size={ButtonSize.MEDIUM}
          onClick={handleCancel}
        >
          {t('buttons.cancel')}
        </Button>

        <Button kind={ButtonKind.SECONDARY} size={ButtonSize.MEDIUM}
          destructive={true}
          disabled={!self || !checkExitSurveyData || !formIsValid || deleteAccount.isPending}
          onClick={handleSubmit}
        >
          {t('buttons.confirm')}
        </Button>
      </div>
    </div>
  )
}